// geoUtils.js
// GPS and geolocation utility functions for time tracking

// ============================================================================
// Constants
// ============================================================================

const EARTH_RADIUS_METERS = 6371000; // Earth's radius in meters
const METERS_PER_FOOT = 0.3048;
const FEET_PER_METER = 3.28084;

// ============================================================================
// Geolocation
// ============================================================================

/**
 * Get current GPS position as a Promise
 * @param {Object} options - Geolocation options
 * @param {boolean} options.enableHighAccuracy - Request high accuracy (default: true)
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
 * @param {number} options.maximumAge - Max age of cached position in ms (default: 0)
 * @returns {Promise<Object>} { lat, lng, accuracy_m }
 * @throws {Error} GeolocationError with code and message
 */
export const getCurrentPosition = (options = {}) => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new GeolocationError('UNAVAILABLE', 'Geolocation is not supported by this browser'));
            return;
        }

        const defaultOptions = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };

        const geoOptions = { ...defaultOptions, ...options };

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy_m: position.coords.accuracy
                });
            },
            (error) => {
                let errorType;
                let errorMessage;

                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorType = 'PERMISSION_DENIED';
                        errorMessage = 'Location permission was denied. Please enable location access in your browser settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorType = 'POSITION_UNAVAILABLE';
                        errorMessage = 'Location information is unavailable. Please check your GPS settings.';
                        break;
                    case error.TIMEOUT:
                        errorType = 'TIMEOUT';
                        errorMessage = 'Location request timed out. Please try again.';
                        break;
                    default:
                        errorType = 'UNKNOWN';
                        errorMessage = 'An unknown error occurred while getting location.';
                }

                reject(new GeolocationError(errorType, errorMessage));
            },
            geoOptions
        );
    });
};

/**
 * Custom error class for geolocation errors
 */
export class GeolocationError extends Error {
    constructor(code, message) {
        super(message);
        this.name = 'GeolocationError';
        this.code = code;
    }
}

// ============================================================================
// Distance Calculations
// ============================================================================

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of point 1 (degrees)
 * @param {number} lng1 - Longitude of point 1 (degrees)
 * @param {number} lat2 - Latitude of point 2 (degrees)
 * @param {number} lng2 - Longitude of point 2 (degrees)
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
    // Convert degrees to radians
    const toRad = (deg) => deg * (Math.PI / 180);

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_METERS * c;
};

/**
 * Check if a position is within a geofence radius of a target location
 * @param {Object} position - Current position { lat, lng }
 * @param {Object} target - Target location { lat, lng }
 * @param {number} radiusMeters - Geofence radius in meters
 * @returns {boolean} True if within radius
 */
export const isWithinGeofence = (position, target, radiusMeters) => {
    const distance = calculateDistance(
        position.lat,
        position.lng,
        target.lat,
        target.lng
    );
    return distance <= radiusMeters;
};

// ============================================================================
// Unit Conversions
// ============================================================================

/**
 * Convert meters to feet
 * @param {number} meters - Distance in meters
 * @returns {number} Distance in feet
 */
export const metersToFeet = (meters) => {
    return meters * FEET_PER_METER;
};

/**
 * Convert feet to meters
 * @param {number} feet - Distance in feet
 * @returns {number} Distance in meters
 */
export const feetToMeters = (feet) => {
    return feet * METERS_PER_FOOT;
};

// ============================================================================
// Formatting
// ============================================================================

/**
 * Format distance for display (auto-selects appropriate unit)
 * @param {number} meters - Distance in meters
 * @param {boolean} useFeet - Use feet instead of meters (default: true for US)
 * @returns {string} Formatted distance string (e.g., "150 ft" or "45 m")
 */
export const formatDistance = (meters, useFeet = true) => {
    if (useFeet) {
        const feet = metersToFeet(meters);
        if (feet >= 5280) {
            const miles = feet / 5280;
            return `${miles.toFixed(1)} mi`;
        }
        return `${Math.round(feet)} ft`;
    } else {
        if (meters >= 1000) {
            const km = meters / 1000;
            return `${km.toFixed(1)} km`;
        }
        return `${Math.round(meters)} m`;
    }
};

// ============================================================================
// Reverse geocoding
// ============================================================================

/**
 * Reverse-geocode lat/lng to an address via Google Geocoding API.
 * Parses the first US-formatted result into the same shape that
 * the Places Autocomplete handler uses, so callers can re-use one
 * "fill this form" code path.
 *
 * @returns {Promise<{
 *   address_line1: string,
 *   address_city: string,
 *   address_state: string,
 *   address_zip: string,
 *   lat: number,
 *   lng: number,
 *   formatted_address: string
 * } | null>}
 */
export const reverseGeocode = async (lat, lng) => {
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    if (!apiKey) throw new Error('Google API key missing');

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);

    const data = await response.json();
    if (data.status !== 'OK' || !data.results || data.results.length === 0) return null;

    const result = data.results[0];
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let zip = '';

    (result.address_components || []).forEach(c => {
        const types = c.types || [];
        if (types.includes('street_number')) streetNumber = c.long_name;
        if (types.includes('route')) route = c.long_name;
        if (types.includes('locality')) city = c.long_name;
        if (types.includes('administrative_area_level_1')) state = c.short_name;
        if (types.includes('postal_code')) zip = c.long_name;
    });

    return {
        address_line1: [streetNumber, route].filter(Boolean).join(' '),
        address_city: city,
        address_state: state,
        address_zip: zip,
        lat,
        lng,
        formatted_address: result.formatted_address,
    };
};

/**
 * Forward-geocode an address string to lat/lng via Google Geocoding API.
 * Returns null if the address can't be resolved.
 *
 * @returns {Promise<{ lat: number, lng: number, formatted_address: string } | null>}
 */
export const forwardGeocode = async (address) => {
    if (!address) return null;
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    if (!apiKey) throw new Error('Google API key missing');

    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`);

    const data = await response.json();
    if (data.status !== 'OK' || !data.results || data.results.length === 0) return null;

    const result = data.results[0];
    return {
        lat: result.geometry?.location?.lat,
        lng: result.geometry?.location?.lng,
        formatted_address: result.formatted_address,
    };
};

/**
 * Resolve an IANA timezone name (e.g. "Pacific/Honolulu") for a lat/lng via
 * the Google Time Zone API. Used when creating/editing a mobile location so
 * the location's `timezone` column is populated from the entered base address.
 *
 * @returns {Promise<string | null>} timezone id or null on failure
 */
export const lookupTimezone = async (lat, lng) => {
    if (lat == null || lng == null) return null;
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    if (!apiKey) return null;

    const timestamp = Math.floor(Date.now() / 1000);
    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`;
    try {
        const response = await fetch(url);
        if (!response.ok) return null;
        const data = await response.json();
        if (data.status !== 'OK') return null;
        return data.timeZoneId || null;
    } catch {
        return null;
    }
};

// ============================================================================
// Default Export
// ============================================================================

const GeoUtils = {
    getCurrentPosition,
    GeolocationError,
    calculateDistance,
    isWithinGeofence,
    metersToFeet,
    feetToMeters,
    formatDistance,
    reverseGeocode,
    forwardGeocode,
    lookupTimezone,
};

export default GeoUtils;
